import type { SessionUser } from "@/api/types";

/**
 * The last `SessionUser` the API confirmed, kept so an authenticated screen can survive
 * a RELOAD with no network — the same gap `referenceCache.ts` closed for `/entry`,
 * one layer up the stack.
 *
 * WHY THIS EXISTS. `/entry` never calls `useSession` at all (App.tsx's own routing
 * note), so referenceCache alone was enough to make its offline reload work. `/time`
 * (ef-017's AC8) is the first route this app renders that BOTH needs a real session
 * AND is expected to survive a reload with no network: `GET /api/auth/me` is a
 * `/api/*` call, which `sw.js`'s own fetch handler deliberately never intercepts
 * (live data, on purpose), so it fails exactly like any other API call offline —
 * and `useSession.ts` already has a NAMED state for that failure, `"unreachable"`,
 * which renders `App.tsx`'s "Can't reach EasyFarm right now" screen. That is the
 * right answer for a stranger who was never signed in on this device; it is the
 * wrong answer for a worker reopening a screen they were already using when the
 * signal dropped, which is precisely what a phone left in a pocket does.
 *
 * WHY localStorage RATHER THAN A QUEUE. Same reasoning as referenceCache.ts: this is
 * one snapshot, overwritten wholesale, read SYNCHRONOUSLY so the first paint is never
 * the error screen it exists to avoid.
 *
 * WHAT THIS DOES NOT DO. It does not extend, forge, or verify a session — it is a
 * client-side RENDERING HINT only, exactly as load-bearing as the reference-data
 * cache: "who this device last successfully spoke to the server as." The API is,
 * and remains, the only authority on whether the underlying cookie still works; the
 * very next request any authenticated screen makes (e.g. `GET /api/demo/time/mine`)
 * either succeeds against that real session or fails, same as it always has.
 */

const KEY = "easyfarm.session.v1";

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function writeSessionSnapshot(user: SessionUser): void {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(KEY, JSON.stringify(user));
  } catch {
    // Quota or private mode. The app is fully functional online right now; it just
    // will not survive an offline reload. Not worth a visible error over.
  }
}

/** Called on an explicit logout, and on a REACHABLE "not signed in" answer (401) —
 *  never on "could not ask" (see useSession.ts), which must leave a stale-but-real
 *  snapshot in place rather than erase the one thing that lets a reload recover. */
export function clearSessionSnapshot(): void {
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(KEY);
  } catch {
    // Nothing to do — see writeSessionSnapshot.
  }
}

/** Returns null on anything unexpected rather than throwing — a corrupted entry
 *  must not be worse than an absent one (same discipline as referenceCache.ts). */
export function readSessionSnapshot(): SessionUser | null {
  const store = storage();
  if (!store) return null;
  let raw: string | null;
  try {
    raw = store.getItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<SessionUser>;
    if (typeof parsed.email !== "string") return null;
    if (parsed.role !== "owner" && parsed.role !== "employee") return null;
    return { email: parsed.email, role: parsed.role };
  } catch {
    return null;
  }
}
